/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import HFIcon from '../icons';
import { readableInk } from '../derivations';

// =============================================================================
// HFTimeline — cronograma planejado (Gantt) da roteirização (handoff 0037 §B).
//
// Zero backend novo: tudo vem do snapshot já adaptado. Uma faixa por rota
// (subcluster); cada parada é um bloco início→conclusão posicionado no eixo do
// tempo. O gap entre blocos é o deslocamento + espera por janela.
//
//   previsão de início    = estimated_arrival_time  (stop.eta, "HH:MM")
//   previsão de conclusão = início + estimated_duration_minutes (stop.dur)
//
// A timeline é só visualização (read-only). Clicar num bloco abre a ficha da
// parada (mesmo StopDrawer do mapa).
// =============================================================================

const PX_PER_MIN = 4; // 240px por hora — largura confortável, com scroll-x.
const LABEL_WIDTH = 208; // coluna fixa de rótulo da rota (sticky à esquerda).

// "HH:MM" ou "HH:MM:SS" → minutos desde a meia-noite. Inválido/"—" → null.
function timeToMinutes(value) {
   const m = /^(\d{2}):(\d{2})/.exec(String(value ?? '').trim());
   if (!m) return null;
   return Number(m[1]) * 60 + Number(m[2]);
}

function minutesToHHMM(min) {
   if (min == null) return '—';
   const wrapped = ((Math.round(min) % 1440) + 1440) % 1440;
   const h = Math.floor(wrapped / 60);
   const m = wrapped % 60;
   return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Minutos-desde-a-meia-noite agora (com segundos pra a régua deslizar suave).
function nowInMinutes() {
   const d = new Date();
   return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

// 'YYYY-MM-DD' de hoje (local) — pra gatear a régua só no dia do roteiro.
function todayYmd() {
   const d = new Date();
   const p = (n) => String(n).padStart(2, '0');
   return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function formatTotals(routeTotals) {
   if (!routeTotals) return null;
   const parts = [];
   if (routeTotals.distanceMeters != null) {
      parts.push(`${(routeTotals.distanceMeters / 1000).toFixed(1)} km`);
   }
   if (routeTotals.durationSeconds != null) {
      parts.push(`${Math.round(routeTotals.durationSeconds / 60)} min`);
   }
   return parts.length ? parts.join(' · ') : null;
}

const VEHICLE_LABEL = { moto: 'Moto', carro: 'Carro' };

export default function HFTimeline({
   subclusters = [],
   stopsBySubcluster = {},
   scheduleDate = null,
   onStopClick = null,
}) {
   // Relógio da régua "agora": atualiza a cada 30s enquanto a timeline está
   // montada (só renderiza com a aba Timeline aberta).
   const [nowMin, setNowMin] = useState(nowInMinutes);
   useEffect(() => {
      const id = setInterval(() => setNowMin(nowInMinutes()), 30000);
      return () => clearInterval(id);
   }, []);
   // ─── Monta as faixas e calcula o intervalo global de tempo ───────────────
   const { lanes, rangeStart, rangeEnd } = useMemo(() => {
      let minT = Infinity;
      let maxT = -Infinity;

      const built = (subclusters || []).map((sub) => {
         const stops = (stopsBySubcluster[sub.id] || [])
            .slice()
            .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

         const blocks = stops
            .map((stop) => {
               const start = timeToMinutes(stop.eta);
               if (start == null) return null;
               const dur = Number(stop.dur) || 0;
               const end = start + dur;
               if (start < minT) minT = start;
               if (end > maxT) maxT = end;
               return { stop, start, end, dur };
            })
            .filter(Boolean);

         const departure = timeToMinutes(sub.routeDepartureTime);
         if (departure != null) {
            if (departure < minT) minT = departure;
            if (departure > maxT) maxT = departure;
         }

         return { sub, blocks, departure };
      });

      if (minT === Infinity) {
         return { lanes: built, rangeStart: null, rangeEnd: null };
      }
      // Arredonda para a hora cheia (margem visual) e garante ≥ 1h de janela.
      const start = Math.floor(minT / 60) * 60;
      let end = Math.ceil(maxT / 60) * 60;
      if (end <= start) end = start + 60;
      return { lanes: built, rangeStart: start, rangeEnd: end };
   }, [subclusters, stopsBySubcluster]);

   const hasData =
      rangeStart != null && lanes.some((l) => l.blocks.length > 0);

   if (!hasData) {
      return (
         <div className="hf-timeline-empty">
            <div className="ico">{HFIcon.clock}</div>
            <div className="ttl">Timeline planejada indisponível</div>
            <div className="sub">
               O cronograma aparece depois de otimizar a roteirização — é a
               otimização que calcula a previsão de início (ETA) por parada.
            </div>
         </div>
      );
   }

   const totalMinutes = rangeEnd - rangeStart;
   const trackWidth = totalMinutes * PX_PER_MIN;

   // Marcas de hora no eixo superior.
   const ticks = [];
   for (let t = rangeStart; t <= rangeEnd; t += 60) {
      ticks.push(t);
   }

   // Régua "agora" — só no DIA do roteiro (a hora-do-dia bate com o gantt) e
   // quando o instante atual cai dentro da janela visível. Será muito mais útil
   // quando o gantt ganhar o tempo real (planejado × executado).
   const showNow =
      scheduleDate === todayYmd() &&
      nowMin >= rangeStart &&
      nowMin <= rangeEnd;
   const nowLeft = (nowMin - rangeStart) * PX_PER_MIN;

   const lanesWithBlocks = lanes.filter((l) => l.blocks.length > 0);

   return (
      <div className="hf-timeline">
         <div className="hf-timeline-scroll">
            <div
               className="hf-timeline-inner"
               style={{ '--track-w': `${trackWidth}px`, '--label-w': `${LABEL_WIDTH}px` }}
            >
               {/* Eixo do tempo (sticky no topo) */}
               <div className="hf-tl-axis">
                  <div className="hf-tl-axis-label">Rota</div>
                  <div className="hf-tl-axis-track" style={{ width: trackWidth }}>
                     {ticks.map((t) => (
                        <div
                           key={t}
                           className="hf-tl-tick"
                           style={{ left: (t - rangeStart) * PX_PER_MIN }}
                        >
                           <span className="hf-tl-tick-lbl">
                              {minutesToHHMM(t)}
                           </span>
                        </div>
                     ))}
                     {showNow && (
                        <div
                           className="hf-tl-now hf-tl-now-axis"
                           style={{ left: nowLeft }}
                        >
                           <span className="hf-tl-now-lbl">
                              {minutesToHHMM(nowMin)}
                           </span>
                        </div>
                     )}
                  </div>
               </div>

               {/* Uma faixa por rota */}
               {lanesWithBlocks.map(({ sub, blocks, departure }) => {
                  const color = sub.color || '#b53636';
                  const ink = readableInk(color);
                  const totals = formatTotals(sub.routeTotals);
                  // Posições visuais (left/width) já resolvidas — usadas pelos
                  // blocos E pelas linhas de deslocamento entre eles.
                  const placed = blocks.map((b) => ({
                     ...b,
                     left: (b.start - rangeStart) * PX_PER_MIN,
                     width: Math.max(b.dur * PX_PER_MIN, 34),
                  }));
                  const departLeft =
                     departure != null
                        ? (departure - rangeStart) * PX_PER_MIN
                        : null;
                  // Linhas de deslocamento: base→1ª parada e entre paradas
                  // consecutivas. O tempo é o travel da parada de DESTINO
                  // (travel_duration_seconds = deslocamento até a parada).
                  const links = [];
                  placed.forEach((blk, i) => {
                     const travelMin =
                        blk.stop.travelSeconds != null
                           ? Math.round(blk.stop.travelSeconds / 60)
                           : null;
                     const x2 = blk.left;
                     let x1;
                     let fromLabel;
                     if (i === 0) {
                        if (departLeft == null) return;
                        x1 = departLeft;
                        fromLabel = 'Da base';
                     } else {
                        const prev = placed[i - 1];
                        x1 = prev.left + prev.width;
                        fromLabel = `De: Pedido ${prev.stop.id}`;
                     }
                     const w = x2 - x1;
                     if (w < 3) return; // paradas coladas — sem linha visível.
                     const tip = [
                        'Deslocamento',
                        fromLabel,
                        `Para: Pedido ${blk.stop.id}`,
                        travelMin != null ? `${travelMin} minutos` : null,
                     ]
                        .filter(Boolean)
                        .join('\n');
                     links.push({ key: `lnk-${blk.stop.id}`, left: x1, width: w, tip });
                  });
                  return (
                     <div key={sub.id} className="hf-tl-lane">
                        <div className="hf-tl-lane-label">
                           <span
                              className="hf-tl-swatch"
                              style={{ background: color }}
                           />
                           <div className="hf-tl-lane-meta">
                              <div className="nm" title={sub.label}>
                                 {sub.label}
                              </div>
                              <div className="sub">
                                 {sub.technician?.name || 'Sem técnico'}
                                 {sub.vehicleClass
                                    ? ` · ${VEHICLE_LABEL[sub.vehicleClass] || sub.vehicleClass}`
                                    : ''}
                              </div>
                              {totals && <div className="totals">{totals}</div>}
                           </div>
                        </div>
                        <div
                           className="hf-tl-lane-track"
                           style={{ width: trackWidth }}
                        >
                           {/* Linhas de hora (grade) */}
                           {ticks.map((t) => (
                              <div
                                 key={t}
                                 className="hf-tl-grid"
                                 style={{ left: (t - rangeStart) * PX_PER_MIN }}
                              />
                           ))}

                           {/* Marca de saída da base (T0) */}
                           {departLeft != null && (
                              <div
                                 className="hf-tl-depart"
                                 style={{ left: departLeft }}
                                 title={`Saída da base (T0): ${minutesToHHMM(departure)}`}
                              >
                                 <span className="flag">T0</span>
                              </div>
                           )}

                           {/* Linhas de deslocamento (com tooltip no hover) */}
                           {links.map((lnk) => (
                              <div
                                 key={lnk.key}
                                 className="hf-tl-link"
                                 style={{
                                    left: lnk.left,
                                    width: lnk.width,
                                    '--c': color,
                                 }}
                                 title={lnk.tip}
                              />
                           ))}

                           {/* Blocos de atendimento */}
                           {placed.map(({ stop, start, end, dur, left, width }) => {
                              const waitMin =
                                 stop.waitSeconds != null
                                    ? Math.round(stop.waitSeconds / 60)
                                    : null;
                              const travelMin =
                                 stop.travelSeconds != null
                                    ? Math.round(stop.travelSeconds / 60)
                                    : null;
                              const tip = [
                                 `${stop.nm}`,
                                 `${minutesToHHMM(start)}–${minutesToHHMM(end)} (${dur} min)`,
                                 travelMin != null
                                    ? `Deslocamento: ${travelMin} min`
                                    : null,
                                 waitMin ? `Espera por janela: ${waitMin} min` : null,
                              ]
                                 .filter(Boolean)
                                 .join('\n');
                              return (
                                 <button
                                    key={stop.id}
                                    type="button"
                                    className="hf-tl-block"
                                    style={{
                                       left,
                                       width,
                                       background: color,
                                       color: ink,
                                    }}
                                    title={tip}
                                    onClick={() => onStopClick?.(stop, sub)}
                                 >
                                    <span className="seq">{stop.seq}</span>
                                    <span className="nm">{stop.nm}</span>
                                 </button>
                              );
                           })}

                           {/* Régua da hora atual — por último no DOM pra ficar
                               acima dos blocos (mesmo z-index), mas a coluna de
                               rótulo sticky (z maior) a cobre ao rolar. */}
                           {showNow && (
                              <div
                                 className="hf-tl-now"
                                 style={{ left: nowLeft }}
                                 aria-hidden="true"
                              />
                           )}
                        </div>
                     </div>
                  );
               })}
            </div>
         </div>
      </div>
   );
}

HFTimeline.propTypes = {
   subclusters: PropTypes.array,
   stopsBySubcluster: PropTypes.object,
   scheduleDate: PropTypes.string,
   onStopClick: PropTypes.func,
};
